import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { StoreModelNames } from "../../dali/type/dali.type";
import { CategoryStoreModel } from "../../../models/store/category.store.model";
import { computed, signal } from "@angular/core";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { type IModelView } from "@bitwarden/web-vault/app/models/view/model.view";
import { StoreModel } from "@bitwarden/web-vault/app/services/dali/vault-parser/parser.type";
import {
  DEFAULT_CATEGORY_ID,
  DEFAULT_CATEGORY_NAME,
  GLOSS_CREDITS_CATEGORY_ID,
  GLOSS_CREDITS_CATEGORY_NAME,
} from "@bitwarden/web-vault/app/models/constants/category.constants";
import { CategoryUtils } from "@bitwarden/web-vault/app/models/view/category/category.utils";

export class CategoriesStoreCollection extends StoreModelCollection {
  readonly type: StoreModelNames = "CategoryStoreModel";
  protected models: Map<string, CategoryStoreModel> = new Map<string, CategoryStoreModel>();

  private _categoryViews = signal<CategoryView[]>(null);
  readonly categoryViews = this._categoryViews.asReadonly();

  categoryViewsSorted = computed(() =>
    [...(this.categoryViews() ?? [])].sort((a, b) => a.name?.localeCompare(b.name)),
  );

  categoriesWithoutDefault = computed(() =>
    this.categoryViewsSorted().filter(
      (category) =>
        category.id !== DEFAULT_CATEGORY_ID && category.id !== GLOSS_CREDITS_CATEGORY_ID,
    ),
  );

  async initialize(): Promise<void> {
    if (!this.models.has(DEFAULT_CATEGORY_ID)) {
      await this.createDefaultCategory();
    }
    await this.ensureGlossCreditsCategory();
  }

  /**
   * Ensure the "GLOSS Credits" system category record exists for every user
   * (first-login and returning). Credit transactions are mapped to this
   * category id, so the record must exist for the name to resolve.
   */
  async ensureGlossCreditsCategory(): Promise<void> {
    if (this.models.has(GLOSS_CREDITS_CATEGORY_ID)) {
      return;
    }
    try {
      const saved = await this.save(CategoryUtils.getGlossCreditsCategoryView());
      if (!saved) {
        this.log.error("Failed to save GLOSS Credits category");
      }
    } catch (e) {
      this.log.error("Error creating GLOSS Credits category");
      this.log.error(e);
    }
  }

  /**
   * Need to double-check that we don't save over the name of the default category if it is changed
   * @param view
   */
  override async save(
    view: IModelView,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<StoreModel> {
    // This collection only ever receives CategoryView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const categoryView = view as CategoryView;
    if (categoryView.id === DEFAULT_CATEGORY_ID) {
      categoryView.name = DEFAULT_CATEGORY_NAME;
      categoryView.generatedDefault = true;
    }
    if (categoryView.id === GLOSS_CREDITS_CATEGORY_ID) {
      categoryView.name = GLOSS_CREDITS_CATEGORY_NAME;
    }
    return await super.save(categoryView, organizationId, collectionIds);
  }

  /**
   * Need to ensure we don't delete the default category
   */
  override async delete(view: IModelView): Promise<boolean> {
    if (view.id !== DEFAULT_CATEGORY_ID && view.id !== GLOSS_CREDITS_CATEGORY_ID) {
      if (!this.categoryInUse(view.id)) {
        return await super.delete(view);
      }
    }
    return false;
  }

  /**
   * Need to ensure we don't delete the default category
   */
  override async deleteFromVault(views: IModelView[]): Promise<boolean> {
    // This collection only ever receives CategoryView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const categoryViews = views as CategoryView[];
    categoryViews.forEach((view) => {
      if (this.categoryInUse(view.id)) {
        this.log.error(`Category ${view.name} is in use and cannot be deleted`);
        throw new Error(`Category ${view.name} is in use and cannot be deleted`);
      }
    });

    const filteredViews = categoryViews.filter(
      (view) => view.id !== DEFAULT_CATEGORY_ID && view.id !== GLOSS_CREDITS_CATEGORY_ID,
    );
    return await super.deleteFromVault(filteredViews);
  }

  /**
   * Need to make sure we don't change the name of the default category if it is changed
   */
  override async saveToVault(
    views: IModelView[],
    triggerChanges = false,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<boolean> {
    // This collection only ever receives CategoryView instances; the base signature is
    // IModelView so the override stays sound for callers holding a StoreModelCollection ref.
    const categoryViews = [...(views as CategoryView[])];
    const defaultView = categoryViews.find((view) => view.id === DEFAULT_CATEGORY_ID);
    if (defaultView) {
      defaultView.name = DEFAULT_CATEGORY_NAME;
      defaultView.generatedDefault = true;
    } else if (!this.models.has(DEFAULT_CATEGORY_ID)) {
      // Ensure the default category is always included
      categoryViews.push(CategoryUtils.getDefaultCategoryView());
    }
    const glossCreditsView = categoryViews.find((view) => view.id === GLOSS_CREDITS_CATEGORY_ID);
    if (glossCreditsView) {
      glossCreditsView.name = GLOSS_CREDITS_CATEGORY_NAME;
    }
    return await super.saveToVault(categoryViews, triggerChanges, organizationId, collectionIds);
  }

  triggerChanges(): void {
    const views: CategoryView[] = [];
    this.models.forEach((model) => {
      views.push(this.deepFreeze(new CategoryView(model)));
    });
    this._categoryViews.set(views);
  }

  clear(): void {
    this.models.clear();
    this._categoryViews.set(null);
  }

  async createDefaultCategory() {
    // Ensure there is always at least the default category
    const defaultCategory = CategoryUtils.getDefaultCategoryView();
    try {
      const savedDefault = await this.save(defaultCategory);
      if (!savedDefault) {
        this.log.error("Failed to save default category");
      }
    } catch (e) {
      this.log.error("Error creating default category");
      this.log.error(e);
    }
  }

  /**
   * TODO: check that there are no transactions that are using this category
   * and no accounts where this is the default category.
   * We may choose to do this from the UI side instead of here
   * @param categoryId
   * @return boolean - returns true if the category is in use and false if not
   */
  categoryInUse(categoryId: string): boolean {
    // TODO: write code to check the transactions and accounts store collections
    return false;
  }
}
